import mobileUsBadge from '@/assets/badges/goodfirms-industry-leader-mobile-us.webp';
import webDeBadge from '@/assets/badges/goodfirms-industry-leader-web-de.webp';
import softwareDeBadge from '@/assets/badges/goodfirms-market-influencer-software-de.webp';
import aiNlpUsBadge from '@/assets/badges/goodfirms-upcoming-achiever-ai-nlp-us.webp';

/**
 * Official GoodFirms leaders-matrix awards. Each links to the matrix it was
 * awarded from. Earned awards, so links are plain outbound citations — not
 * rel="sponsored". `label` doubles as alt text and the schema.org `award` string.
 */
export const GOODFIRMS_AWARDS = {
  'mobile-us': {
    image: mobileUsBadge,
    label: 'GoodFirms Industry Leader — Mobile App Development in the United States',
    href: 'https://www.goodfirms.co/directory/country/app-development/us#leaders-matrix',
  },
  'web-de': {
    image: webDeBadge,
    label: 'GoodFirms Industry Leader — Web Development in Delaware',
    href: 'https://www.goodfirms.co/leaders-matrix/share/MTc1Mjg2fDJ8MTQ1NDl8MTc5MDAzNTMxMzg2MA.5Sooni6sOU3sTc-_TJ5g0DJNaHH2Y0YEcU2-H4j-yog',
  },
  'software-de': {
    image: softwareDeBadge,
    label: 'GoodFirms Market Influencer — Software Development in Delaware',
    href: 'https://www.goodfirms.co/leaders-matrix/share/MTc1Mjg2fDN8MTg3MjV8MTc5MDAzNTQwMTY3MA.3ExR8F-1jjiaCyVwEdqCrVQvNdMFUo-tZ8fvxSdmbiI',
  },
  'ai-nlp-us': {
    image: aiNlpUsBadge,
    label: 'GoodFirms Upcoming Achiever — Artificial Intelligence (NLP) in the United States',
    href: 'https://www.goodfirms.co/leaders-matrix/share/MTc1Mjg2fDIxfDk5NzF8MTc5MDAzNTU0OTIxNQ.31iog9HwOxt-1erRJ1OV-kPkfQaxFtL9HjeajUtZiFc',
  },
} as const;

export type GoodFirmsAwardId = keyof typeof GOODFIRMS_AWARDS;

/** Service slug → the award that belongs in that service page's hero. */
export const SERVICE_AWARD: Partial<Record<string, { id: GoodFirmsAwardId; blurb: string }>> = {
  'mobile-app-development': { id: 'mobile-us', blurb: 'Named an Industry Leader for mobile app development in the United States by GoodFirms.' },
  'web-development': { id: 'web-de', blurb: 'Named an Industry Leader for web development in Delaware by GoodFirms.' },
  'custom-app-development': { id: 'software-de', blurb: 'Named a Market Influencer for software development in Delaware by GoodFirms.' },
  'ai-ml-solutions': { id: 'ai-nlp-us', blurb: 'Named an Upcoming Achiever for AI (NLP) in the United States by GoodFirms.' },
};

interface GoodFirmsBadgeProps {
  award?: GoodFirmsAwardId;
  /** Rendered width in px; assets are 400px wide, so keep this ≤ 200 for crisp 2x. */
  size?: number;
  className?: string;
  /** Hero placements should load eagerly; everything else lazy. */
  eager?: boolean;
}

export function GoodFirmsBadge({ award = 'mobile-us', size = 160, className = '', eager = false }: GoodFirmsBadgeProps) {
  const { image, label, href } = GOODFIRMS_AWARDS[award];
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-block shrink-0 transition-transform duration-300 hover:scale-105 ${className}`}
      aria-label={`${label} (opens GoodFirms leaders matrix)`}
    >
      <img
        src={image}
        alt={label}
        width={size}
        height={size}
        loading={eager ? 'eager' : 'lazy'}
        decoding="async"
        className="h-auto drop-shadow-xl"
        style={{ width: size }}
      />
    </a>
  );
}
